import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import DocumentShell from '../features/documents/DocumentShell'
import DocumentSection from '../features/documents/DocumentSection'
import DocumentFieldGrid from '../features/documents/DocumentFieldGrid'
import DocumentStatusBadge from '../features/documents/DocumentStatusBadge'
import { formatDate, safeText, shortId } from '../features/documents/documentFormatters'

function fileSizeLabel(size?: number | null) {
  if (typeof size !== 'number' || !Number.isFinite(size)) return 'Unknown size'
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${size} bytes`
}

export default function PodViewerPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const qc = useQueryClient()
  const { hasRole } = useAuth()
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null)
  const [uploadMessage, setUploadMessage] = useState<string | null>(null)

  const { data: job, isLoading: jobLoading } = useQuery({
    queryKey: ['pod-job', jobId],
    queryFn: async () => (await api.jobs.get(jobId!)).data,
    enabled: !!jobId,
  })

  const { data: podMeta, isLoading: podMetaLoading, error: podMetaError } = useQuery({
    queryKey: ['pod-meta', jobId],
    queryFn: async () => (await api.jobs.getPod(jobId!)).data,
    enabled: !!jobId,
  })

  const { data: podList, isLoading: podListLoading } = useQuery({
    queryKey: ['pod-list', jobId],
    queryFn: async () => (await api.jobs.listPodDocuments(jobId!)).data,
    enabled: !!jobId,
  })

  const documents = useMemo(() => {
    const listed = podList?.documents ?? []
    if (listed.length > 0) return listed
    if (podMeta?.documents?.length) return podMeta.documents
    return podMeta?.file ? [podMeta.file] : []
  }, [podList, podMeta])

  useEffect(() => {
    if (!documents.length) {
      setSelectedDocumentId(null)
      return
    }
    if (!selectedDocumentId || !documents.some((document) => document.id === selectedDocumentId)) {
      setSelectedDocumentId(documents[0].id ?? null)
    }
  }, [documents, selectedDocumentId])

  const selectedDocument = useMemo(
    () => documents.find((document) => document.id === selectedDocumentId) ?? documents[0] ?? null,
    [documents, selectedDocumentId]
  )

  const { data: load } = useQuery({
    queryKey: ['pod-load', job?.loadId],
    queryFn: async () => (await api.loads.get(job!.loadId)).data,
    enabled: !!job?.loadId,
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['pod-invoices'],
    queryFn: async () => (await api.invoices.list()).data,
  })

  const { data: podBlob, isLoading: podLoading, error: podError } = useQuery({
    queryKey: ['pod-file', jobId, selectedDocument?.id],
    queryFn: async () => (await api.jobs.downloadPod(jobId!, { documentId: selectedDocument?.id ?? undefined })).data,
    enabled: !!jobId && !!selectedDocument,
  })

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!jobId || !selectedUploadFile) throw new Error('Select a POD file first')
      return api.jobs.uploadPod(jobId, selectedUploadFile)
    },
    onSuccess: async (response) => {
      setUploadMessage(`Uploaded ${response.data.file.filename}`)
      setSelectedUploadFile(null)
      await qc.invalidateQueries({ queryKey: ['pod-meta', jobId] })
      await qc.invalidateQueries({ queryKey: ['pod-list', jobId] })
      await qc.invalidateQueries({ queryKey: ['pod-job', jobId] })
      if (response.data.documentId) {
        setSelectedDocumentId(response.data.documentId)
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!jobId) throw new Error('Job is required')
      return api.jobs.deletePodDocument(jobId, documentId)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['pod-meta', jobId] })
      await qc.invalidateQueries({ queryKey: ['pod-list', jobId] })
      await qc.invalidateQueries({ queryKey: ['pod-file', jobId] })
      await qc.invalidateQueries({ queryKey: ['pod-job', jobId] })
    },
  })

  useEffect(() => {
    if (!podBlob) {
      setObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(podBlob)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [podBlob])

  const handleDownload = async () => {
    if (!jobId || !selectedDocument) return
    const response = await api.jobs.downloadPod(jobId, { documentId: selectedDocument.id ?? undefined, download: true })
    const blobUrl = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = selectedDocument.filename || `pod-${jobId}`
    anchor.click()
    URL.revokeObjectURL(blobUrl)
  }

  const handlePrint = () => {
    if (!objectUrl) return
    const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer')
    if (!popup) return
    const tryPrint = () => {
      try {
        popup.focus()
        popup.print()
      } catch {
        // no-op
      }
    }
    popup.addEventListener('load', tryPrint)
    setTimeout(tryPrint, 700)
  }

  const hasPod = documents.length > 0 || Boolean(podMeta?.uploaded)
  const canDeletePod = hasRole('ADMIN', 'SUPER_ADMIN', 'DISPATCHER', 'AUX_ADMIN', 'BROKER_ADMIN', 'DEALER', 'SHIPPER')
  const activeFilename = selectedDocument?.filename || `POD for job ${shortId(jobId || '')}`
  const activeUploadedAt = selectedDocument?.uploadedAt || podMeta?.file?.uploadedAt || job?.proofOfDeliveryUploadedAt || null
  const activeMimeType = selectedDocument?.mimeType || podBlob?.type || job?.proofOfDeliveryMimeType || ''
  const activeSizeBytes = selectedDocument?.sizeBytes || job?.proofOfDeliverySizeBytes || 0

  if (jobLoading || podMetaLoading || podListLoading) {
    return <div className="container mx-auto p-6 text-gray-500">Loading POD…</div>
  }

  if (!jobId || !job) {
    return <div className="container mx-auto p-6 text-red-600">POD job not found.</div>
  }

  const relatedInvoice = invoices.find((invoice: any) => String(invoice.jobId ?? invoice.job_id) === String(jobId))

  return (
    <DocumentShell
      title="Proof of Delivery"
      subtitle={`Job ${shortId(jobId)} · ${activeFilename}`}
      meta={[
        { label: 'Status', value: <DocumentStatusBadge tone={hasPod ? 'green' : 'amber'} label={hasPod ? 'Uploaded' : 'Missing'} /> },
        { label: 'Filename', value: activeFilename },
        { label: 'Uploaded', value: formatDate(activeUploadedAt) },
        { label: 'Size', value: fileSizeLabel(activeSizeBytes) },
      ]}
      actions={
        <>
          <label className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              onChange={(event) => {
                setSelectedUploadFile(event.target.files?.[0] ?? null)
                setUploadMessage(null)
              }}
              className="hidden"
            />
            Choose POD
          </label>
          <button
            type="button"
            onClick={() => uploadMutation.mutate()}
            disabled={!selectedUploadFile || uploadMutation.isPending}
            className="rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
          </button>
          {objectUrl && (
            <button
              type="button"
              onClick={() => window.open(objectUrl, '_blank', 'noopener,noreferrer')}
              className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Open POD
            </button>
          )}
          {selectedDocument && <button type="button" onClick={handleDownload} className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Download</button>}
          {objectUrl && <button type="button" onClick={handlePrint} className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Print</button>}
          {canDeletePod && selectedDocument?.id && <button type="button" onClick={() => deleteMutation.mutate(selectedDocument.id as string)} className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100" disabled={deleteMutation.isPending}>Delete</button>}
          <Link to={`/jobs/${jobId}/dispatch-sheet`} className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Dispatch Sheet</Link>
          <Link to={`/jobs/${jobId}/bol`} className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">BOL</Link>
          {load?.id && <Link to={`/loads/${load.id}/rate-confirmation`} className="rounded border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Rate Confirmation</Link>}
        </>
      }
    >
      <div className="space-y-4">
        {!hasPod ? (
          <DocumentSection title="POD Status" subtitle="Driver must upload a signed delivery receipt or photo/PDF proof of delivery.">
            <div className="rounded border border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-700">
              No POD uploaded yet.
            </div>
          </DocumentSection>
        ) : null}

        <DocumentSection title="Upload" subtitle="Capture with mobile camera or upload image/PDF.">
          <div className="rounded border border-slate-300 bg-white p-3 text-sm text-slate-700">
            {selectedUploadFile ? (
              <div>
                <div className="font-medium">Selected: {selectedUploadFile.name}</div>
                <div className="text-xs text-slate-500">{fileSizeLabel(selectedUploadFile.size)} · {selectedUploadFile.type || 'unknown type'}</div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">Choose POD to upload from the action bar. Supported: JPG, PNG, WEBP, PDF (max 15 MB).</div>
            )}
            {uploadMessage && <div className="mt-2 rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">{uploadMessage}</div>}
            {uploadMutation.isError && <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{(uploadMutation.error as any)?.response?.data?.error ?? (uploadMutation.error as Error)?.message ?? 'Upload failed.'}</div>}
            {deleteMutation.isError && <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{(deleteMutation.error as any)?.response?.data?.error ?? (deleteMutation.error as Error)?.message ?? 'Delete failed.'}</div>}
            {podMetaError && <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">Failed to load POD metadata.</div>}
          </div>
        </DocumentSection>

        <DocumentSection title="POD Documents" subtitle={`${documents.length} file${documents.length === 1 ? '' : 's'} attached to this job.`}>
          {!documents.length ? (
            <div className="text-sm text-slate-500">No POD uploaded yet.</div>
          ) : (
            <div className="space-y-2">
              {documents.map((document) => (
                <button
                  key={`${document.id || 'legacy'}-${document.uploadedAt || 'time'}`}
                  type="button"
                  onClick={() => setSelectedDocumentId(document.id ?? null)}
                  className={`w-full rounded border px-3 py-2 text-left text-sm ${selectedDocument?.id === document.id ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-white hover:bg-slate-50'}`}
                >
                  <div className="font-medium text-slate-800">{document.filename}</div>
                  <div className="text-xs text-slate-500">{safeText(document.mimeType, 'Unknown type')} · {fileSizeLabel(document.sizeBytes)} · {formatDate(document.uploadedAt)}</div>
                </button>
              ))}
            </div>
          )}
        </DocumentSection>

        <DocumentSection title="POD Details" subtitle="Uploaded delivery proof and related paperwork.">
          <DocumentFieldGrid
            items={[
              { label: 'Uploaded By', value: safeText(selectedDocument?.uploadedBy ?? selectedDocument?.metadata?.uploadedBy, 'Unknown') },
              { label: 'MIME Type', value: safeText(activeMimeType, 'Unknown') },
              { label: 'File Size', value: fileSizeLabel(activeSizeBytes) },
              { label: 'Uploaded At', value: formatDate(activeUploadedAt) },
              { label: 'Related Invoice', value: relatedInvoice?.invoiceNumber ?? relatedInvoice?.invoice_number ?? 'Not linked yet' },
              { label: 'Load / Job', value: `${shortId(load?.id ?? job.loadId)} / ${shortId(job.id)}` },
            ]}
            columns={2}
          />
        </DocumentSection>

        <DocumentSection title="Preview" subtitle="Image or PDF preview when available.">
          <div className="rounded border border-slate-300 bg-white p-3">
            {objectUrl && activeMimeType.startsWith('image/') && (
              <img src={objectUrl} alt={activeFilename} className="max-w-full h-auto rounded" />
            )}
            {objectUrl && activeMimeType === 'application/pdf' && (
              <iframe title={activeFilename} src={objectUrl} className="w-full min-h-[75vh] border-0 rounded" />
            )}
            {objectUrl && !activeMimeType.startsWith('image/') && activeMimeType !== 'application/pdf' && (
              <div className="text-sm text-slate-500">Preview not available for this file type. Use Download.</div>
            )}
            {!objectUrl && podError && (
              <div className="text-sm text-red-600">Failed to load POD file.</div>
            )}
            {!objectUrl && !podError && (
              <div className="text-sm text-slate-500">Open or download to print.</div>
            )}
          </div>
        </DocumentSection>

        <DocumentSection title="Related Documents">
          <div className="flex flex-wrap gap-2 text-sm no-print">
            <Link to={`/jobs/${jobId}/dispatch-sheet`} className="rounded-lg border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50">Dispatch Sheet</Link>
            <Link to={`/jobs/${jobId}/bol`} className="rounded-lg border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50">BOL</Link>
            {load?.id && <Link to={`/loads/${load.id}/rate-confirmation`} className="rounded-lg border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50">Rate Confirmation</Link>}
            {relatedInvoice?.id && <Link to={`/invoices/${relatedInvoice.id}`} className="rounded-lg border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50">Invoice</Link>}
          </div>
        </DocumentSection>
      </div>
    </DocumentShell>
  )
}
